import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import './Dashboard.css';
import './InfluencerProfile.css';
import { MediaKit } from './MediaKit';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.alooma.mg';

// Meta/TikTok OAuth is paused. Reach and recent content come from the July
// media kit the Alooma team links during review. Flip to bring "Connected
// Accounts" back.
const SOCIAL_OAUTH_ENABLED = false;

// Social platforms creators can connect. Add the others back here as each
// integration is approved and configured.
const ENABLED_SOCIAL_PLATFORMS = ['tiktok', 'instagram', 'facebook'];

// Friendly text for the error codes the API returns after a connect attempt
const SOCIAL_ERROR_TEXT = {
  access_denied: 'Connection cancelled.',
  no_instagram_professional_account:
    'No Instagram Creator or Business account was found. Switch your Instagram to a Creator or Business account, link it to a Facebook Page, then try again.',
  token_exchange_failed: 'We could not finish connecting. Please try again.',
  invalid_state: 'The connection expired. Please try again.',
  connection_failed: 'Something went wrong while connecting. Please try again, or contact the Alooma team if it keeps happening.',
};

// Format large numbers (e.g., 1500000 -> 1.5M)
const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
};

// Digits only -> comma-grouped string for money inputs
// Recent posts/videos from a connected account (same shape for every platform)
function RecentMediaGrid({ items, label, platformName }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="recent-media">
      <span className="recent-media-label">{label}</span>
      <div className="recent-media-grid">
        {items.slice(0, 6).map((item) => (
          <a
            key={item.id}
            href={item.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="recent-media-item"
            title={item.title || `View on ${platformName}`}
          >
            {item.coverImageUrl ? (
              <img src={item.coverImageUrl} alt={item.title || `${platformName} post`} />
            ) : (
              <div className="recent-media-placeholder">▶</div>
            )}
            <span className="recent-media-stat">♥ {formatNumber(item.likeCount)}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

const formatMoney = (val) => {
  const digits = String(val ?? '').replace(/[^\d]/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('en-US');
};

function InfluencerProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [socialConnections, setSocialConnections] = useState({
    instagram: null,
    tiktok: null,
    youtube: null,
    facebook: null,
    snapchat: null,
    twitter: null,
  });
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [mediaKit, setMediaKit] = useState(undefined); // undefined while loading
  const [showWelcome, setShowWelcome] = useState(false);
  const messageRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: '',
    bio: '',
    niche: '',
    niches: [],
    customNiches: [],
    instagramHandle: '',
    tiktokHandle: '',
    facebookHandle: '',
    youtubeHandle: '',
    snapchatHandle: '',
    twitterHandle: '',
    birthday: '',
    location: '',
    rates: [{ type: 'per_post', amount: '' }],
    pastPartnerships: [{ brandName: '', campaignType: '', year: '' }],
    paymentMethod: '',
    paymentDetails: '',
  });
  const [customNicheInput, setCustomNicheInput] = useState('');

  const rateTypeOptions = [
    { value: 'per_post', label: 'Per Post' },
    { value: 'per_story', label: 'Per Story' },
    { value: 'per_reel', label: 'Per Reel' },
    { value: 'per_carousel', label: 'Per Carousel' },
    { value: 'per_video', label: 'Per Video' },
    { value: 'per_campaign', label: 'Per Campaign' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'day_rate', label: 'Day Rate' },
    { value: 'usage_rights', label: 'Usage Rights (per month)' },
  ];

  const nicheOptions = [
    'Food & Culinary',
    'Lifestyle',
    'Fitness & Health',
    'Beauty & Fashion',
    'Travel',
    'Technology',
    'Entertainment',
    'Gaming',
    'Parenting & Family',
    'Home & DIY',
    'Finance',
    'Education',
    'Pets & Animals',
    'Sports',
    'Music',
    'Art & Design',
    'Automotive',
    'Sustainability',
  ];

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (!storedUser || !token) {
      navigate('/network');
      return;
    }

    const userData = JSON.parse(storedUser);
    if (userData.userType !== 'influencer') {
      navigate('/network/brand-dashboard');
      return;
    }

    setUser(userData);
    fetchProfile(token);
    fetchMediaKit(token);
    if (SOCIAL_OAUTH_ENABLED) fetchSocialConnections(token);
  }, [navigate]);

  // Scroll any success/error banner into view so users always see feedback
  useEffect(() => {
    if (message.text && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [message]);

  // Handle OAuth callback URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('welcome') === '1') setShowWelcome(true);
    if (!SOCIAL_OAUTH_ENABLED) return;
    const connectedPlatform = params.get('social_connected');
    const socialError = params.get('social_error');

    if (connectedPlatform) {
      setMessage({ type: 'success', text: `Successfully connected ${connectedPlatform}!` });
      // Refresh social connections
      const token = localStorage.getItem('token');
      if (token) fetchSocialConnections(token);
      // Clear URL params
      navigate('/network/influencer-profile', { replace: true });
    } else if (socialError) {
      setMessage({ type: 'error', text: SOCIAL_ERROR_TEXT[socialError] || `Failed to connect: ${socialError}` });
      navigate('/network/influencer-profile', { replace: true });
    }
  }, [location, navigate]);

  const fetchProfile = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.user) {
        setFormData({
          fullName: data.user.fullName || '',
          bio: data.user.bio || '',
          niche: data.user.niche || '',
          niches: data.user.niches || [],
          customNiches: data.user.customNiches || [],
          instagramHandle: data.user.instagramHandle || '',
          tiktokHandle: data.user.tiktokHandle || '',
          facebookHandle: data.user.facebookHandle || '',
          youtubeHandle: data.user.youtubeHandle || '',
          snapchatHandle: data.user.snapchatHandle || '',
          twitterHandle: data.user.twitterHandle || '',
          birthday: data.user.birthday || '',
          location: data.user.location || '',
          rates: data.user.rates?.length ? data.user.rates : [{ type: 'per_post', amount: '' }],
          pastPartnerships: Array.isArray(data.user.pastPartnerships) && data.user.pastPartnerships.length 
            ? data.user.pastPartnerships 
            : [{ brandName: '', campaignType: '', year: '' }],
          paymentMethod: data.user.paymentMethod || '',
          paymentDetails: data.user.paymentDetails || '',
        });
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMediaKit = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/media-kit/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      setMediaKit(response.ok ? data.mediaKit : null);
    } catch (err) {
      console.error('Error fetching media kit:', err);
      setMediaKit(null);
    }
  };

  const fetchSocialConnections = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/social`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.connections) {
        setSocialConnections(data.connections);
      }
    } catch (err) {
      console.error('Error fetching social connections:', err);
    }
  };

  const handleConnectPlatform = async (platform) => {
    setConnectingPlatform(platform);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/social/connect/${platform}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.authUrl) {
        // Redirect to OAuth page
        window.location.href = data.authUrl;
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to initiate connection' });
        setConnectingPlatform(null);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to connect platform' });
      setConnectingPlatform(null);
    }
  };

  const handleDisconnectPlatform = async (platform) => {
    if (!window.confirm(`Are you sure you want to disconnect ${platform}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/social/disconnect/${platform}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        setSocialConnections(prev => ({ ...prev, [platform]: null }));
        setMessage({ type: 'success', text: `Disconnected ${platform} successfully` });
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.message || 'Failed to disconnect' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to disconnect platform' });
    }
  };

  const handleSyncPlatform = async (platform) => {
    setConnectingPlatform(platform);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/social/sync/${platform}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (response.ok && data.connection) {
        setSocialConnections(prev => ({ ...prev, [platform]: data.connection }));
        setMessage({ type: 'success', text: `${platform} metrics updated!` });
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to sync' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to sync platform metrics' });
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setMessage({ type: '', text: '' });
  };

  const toggleNiche = (niche) => {
    setFormData(prev => ({
      ...prev,
      niches: prev.niches.includes(niche)
        ? prev.niches.filter(n => n !== niche)
        : [...prev.niches, niche]
    }));
    setMessage({ type: '', text: '' });
  };

  const addCustomNiche = () => {
    const trimmed = customNicheInput.trim();
    if (trimmed && !formData.customNiches.includes(trimmed) && !formData.niches.includes(trimmed)) {
      setFormData(prev => ({
        ...prev,
        customNiches: [...prev.customNiches, trimmed]
      }));
      setCustomNicheInput('');
      setMessage({ type: '', text: '' });
    }
  };

  const removeCustomNiche = (niche) => {
    setFormData(prev => ({
      ...prev,
      customNiches: prev.customNiches.filter(n => n !== niche)
    }));
  };

  // Rate management functions
  const addRate = () => {
    // Find a rate type that hasn't been used yet
    const usedTypes = formData.rates.map(r => r.type);
    const availableType = rateTypeOptions.find(opt => !usedTypes.includes(opt.value));
    
    if (availableType) {
      setFormData(prev => ({
        ...prev,
        rates: [...prev.rates, { type: availableType.value, amount: '' }]
      }));
    }
  };

  const updateRate = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      rates: prev.rates.map((rate, i) => 
        i === index ? { ...rate, [field]: value } : rate
      )
    }));
    setMessage({ type: '', text: '' });
  };

  const removeRate = (index) => {
    if (formData.rates.length > 1) {
      setFormData(prev => ({
        ...prev,
        rates: prev.rates.filter((_, i) => i !== index)
      }));
    }
  };

  const getAvailableRateTypes = (currentType) => {
    const usedTypes = formData.rates.map(r => r.type).filter(t => t !== currentType);
    return rateTypeOptions.filter(opt => !usedTypes.includes(opt.value));
  };

  // Partnership management functions
  const campaignTypeOptions = [
    'Instagram Feed Post',
    'Instagram Reel',
    'Instagram Story',
    'TikTok Video',
    'YouTube Video',
    'YouTube Shorts',
    'Brand Ambassador',
    'Product Review',
    'Sponsored Content',
    'Collaboration',
    'Event Coverage',
    'Takeover',
    'UGC Campaign',
    'Affiliate',
    'Other',
  ];

  const addPartnership = () => {
    setFormData(prev => ({
      ...prev,
      pastPartnerships: [...prev.pastPartnerships, { brandName: '', campaignType: '', year: '' }]
    }));
  };

  const updatePartnership = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      pastPartnerships: prev.pastPartnerships.map((partnership, i) =>
        i === index ? { ...partnership, [field]: value } : partnership
      )
    }));
    setMessage({ type: '', text: '' });
  };

  const removePartnership = (index) => {
    if (formData.pastPartnerships.length > 1) {
      setFormData(prev => ({
        ...prev,
        pastPartnerships: prev.pastPartnerships.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update profile');
      }

      // Update local storage
      const storedUser = JSON.parse(localStorage.getItem('user'));
      const updatedUser = { ...storedUser, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/network');
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loader"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <img src="/network-logo.png" alt="Alooma Network" className="sidebar-logo-img" />
          </Link>
        </div>

        <nav className="sidebar-nav">
          <Link to="/network/influencer-dashboard" className="nav-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            Browse Jobs
          </Link>
          <Link to="/network/influencer-dashboard" className="nav-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
            </svg>
            My Bids
          </Link>
          <Link to="/network/influencer-dashboard" className="nav-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Messages
          </Link>
          <Link to="/network/influencer-profile" className="nav-item active">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            My Profile
          </Link>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.fullName?.charAt(0) || 'C'}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.fullName || formData.fullName}</span>
              <span className="user-type">Creator</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-content">
            <h1>My Profile</h1>
          </div>
        </header>

        <div className="dashboard-content">
          <div ref={messageRef} />
          {showWelcome && (
            <div className="welcome-banner">
              <h3>Welcome to Alooma Network!</h3>
              <p>Let's set you up for success: complete your profile details, niches and rates below. The Alooma team will add your July media kit when they review your account, so brands can see your reach and recent content. A complete profile gets more brand interest.</p>
              <button className="welcome-dismiss" onClick={() => setShowWelcome(false)}>Got it</button>
            </div>
          )}
          <form onSubmit={handleSubmit} className="profile-form">
          {message.text && (
            <div className={`message ${message.type}`}>
              {message.text}
            </div>
          )}

          {/* Basic Info */}
          <section className="form-section">
            <h2>Basic Information</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="birthday">Birthday</label>
                <input
                  type="date"
                  id="birthday"
                  name="birthday"
                  value={formData.birthday}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="City, State/Province, Country"
              />
            </div>

            <div className="form-group">
              <label htmlFor="bio">Bio</label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Tell brands about yourself, your content style, and what makes you unique..."
                rows={4}
              />
            </div>
          </section>

          {/* Niches / Verticals */}
          <section className="form-section">
            <h2>Niches & Verticals</h2>
            <p className="section-subtitle">Select all categories that apply to your content (helps with brand matching)</p>
            
            <div className="niche-tags">
              {nicheOptions.map((niche) => (
                <button
                  key={niche}
                  type="button"
                  className={`niche-tag ${formData.niches.includes(niche) ? 'selected' : ''}`}
                  onClick={() => toggleNiche(niche)}
                >
                  {niche}
                </button>
              ))}
              {formData.customNiches.map((niche) => (
                <button
                  key={niche}
                  type="button"
                  className="niche-tag selected custom"
                  onClick={() => removeCustomNiche(niche)}
                  title="Click to remove"
                >
                  {niche} ×
                </button>
              ))}
            </div>

            <div className="custom-niche-input">
              <input
                type="text"
                value={customNicheInput}
                onChange={(e) => setCustomNicheInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomNiche(); } }}
                placeholder="Add a custom niche category..."
              />
              <button type="button" onClick={addCustomNiche} disabled={!customNicheInput.trim()}>
                Add
              </button>
            </div>
            <span className="form-hint">Can't find your niche? Add a custom category to improve discoverability</span>
          </section>

          {/* July media kit */}
          <section className="form-section">
            <h2>Your media kit</h2>
            <p className="section-subtitle">
              This is how brands see your reach and recent content. It's linked from July and kept up to date by the Alooma team.
            </p>
            {mediaKit === undefined ? (
              <p className="section-subtitle">Loading media kit…</p>
            ) : (
              <MediaKit
                mediaKit={mediaKit}
                emptyText="The Alooma team will add your July media kit when they review your account."
              />
            )}
          </section>

          {/* Social Media Links */}
          {SOCIAL_OAUTH_ENABLED && (
          <section className="form-section">
            <h2>Connected Accounts</h2>
            <p className="section-subtitle">Connect your TikTok, Instagram and Facebook to show brands your verified followers, engagement and recent posts. More platforms are coming soon.</p>
            
            <div className="social-connections-grid">
              {/* Instagram */}
              {ENABLED_SOCIAL_PLATFORMS.includes('instagram') && (
              <div className={`social-connection-card ${socialConnections.instagram?.is_connected ? 'connected' : ''}`}>
                <div className="social-card-header">
                  <div className="social-icon instagram">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <div className="social-card-info">
                    <h4>Instagram</h4>
                    {socialConnections.instagram?.is_connected ? (
                      <span className="social-username">@{socialConnections.instagram.username}</span>
                    ) : (
                      <span className="social-status">Not connected</span>
                    )}
                  </div>
                  {socialConnections.instagram?.is_verified && (
                    <span className="verified-badge" title="Verified Account">✓</span>
                  )}
                </div>

                {socialConnections.instagram?.is_connected ? (
                  <>
                    <div className="social-metrics">
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.instagram.follower_count)}</span>
                        <span className="metric-label">Followers</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.instagram.post_count)}</span>
                        <span className="metric-label">Posts</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{socialConnections.instagram.engagement_rate || 0}%</span>
                        <span className="metric-label">Engagement</span>
                      </div>
                    </div>
                    <RecentMediaGrid
                      items={socialConnections.instagram.recent_media}
                      label="Recent posts"
                      platformName="Instagram"
                    />
                    <div className="social-card-actions">
                      <button
                        type="button"
                        className="sync-btn"
                        onClick={() => handleSyncPlatform('instagram')}
                        disabled={connectingPlatform === 'instagram'}
                      >
                        {connectingPlatform === 'instagram' ? 'Syncing...' : 'Refresh'}
                      </button>
                      <button
                        type="button"
                        className="disconnect-btn"
                        onClick={() => handleDisconnectPlatform('instagram')}
                      >
                        Disconnect
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="connect-btn instagram"
                    onClick={() => handleConnectPlatform('instagram')}
                    disabled={connectingPlatform === 'instagram'}
                  >
                    {connectingPlatform === 'instagram' ? 'Connecting...' : 'Connect Instagram'}
                  </button>
                )}
                {!socialConnections.instagram?.is_connected && (
                  <p className="social-hint">
                    Requires an Instagram Creator or Business account linked to a Facebook Page.
                    You'll sign in with Facebook.
                  </p>
                )}
              </div>
              )}

              {/* TikTok */}
              {ENABLED_SOCIAL_PLATFORMS.includes('tiktok') && (
              <div className={`social-connection-card ${socialConnections.tiktok?.is_connected ? 'connected' : ''}`}>
                <div className="social-card-header">
                  <div className="social-icon tiktok">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </div>
                  <div className="social-card-info">
                    <h4>TikTok</h4>
                    {socialConnections.tiktok?.is_connected ? (
                      <span className="social-username">@{socialConnections.tiktok.username}</span>
                    ) : (
                      <span className="social-status">Not connected</span>
                    )}
                  </div>
                  {socialConnections.tiktok?.is_verified && (
                    <span className="verified-badge" title="Verified Account">✓</span>
                  )}
                </div>

                {socialConnections.tiktok?.is_connected ? (
                  <>
                    <div className="social-metrics">
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.tiktok.follower_count)}</span>
                        <span className="metric-label">Followers</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.tiktok.post_count)}</span>
                        <span className="metric-label">Videos</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{socialConnections.tiktok.engagement_rate || 0}%</span>
                        <span className="metric-label">Engagement</span>
                      </div>
                    </div>
                    <RecentMediaGrid
                      items={socialConnections.tiktok.recent_media}
                      label="Recent videos"
                      platformName="TikTok"
                    />
                    <div className="social-card-actions">
                      <button
                        type="button"
                        className="sync-btn"
                        onClick={() => handleSyncPlatform('tiktok')}
                        disabled={connectingPlatform === 'tiktok'}
                      >
                        {connectingPlatform === 'tiktok' ? 'Syncing...' : 'Refresh'}
                      </button>
                      <button
                        type="button"
                        className="disconnect-btn"
                        onClick={() => handleDisconnectPlatform('tiktok')}
                      >
                        Disconnect
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="connect-btn tiktok"
                    onClick={() => handleConnectPlatform('tiktok')}
                    disabled={connectingPlatform === 'tiktok'}
                  >
                    {connectingPlatform === 'tiktok' ? 'Connecting...' : 'Connect TikTok'}
                  </button>
                )}
              </div>
              )}

              {/* YouTube */}
              {ENABLED_SOCIAL_PLATFORMS.includes('youtube') && (
              <div className={`social-connection-card ${socialConnections.youtube?.is_connected ? 'connected' : ''}`}>
                <div className="social-card-header">
                  <div className="social-icon youtube">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </div>
                  <div className="social-card-info">
                    <h4>YouTube</h4>
                    {socialConnections.youtube?.is_connected ? (
                      <span className="social-username">{socialConnections.youtube.display_name}</span>
                    ) : (
                      <span className="social-status">Not connected</span>
                    )}
                  </div>
                  {socialConnections.youtube?.is_verified && (
                    <span className="verified-badge" title="Verified Account">✓</span>
                  )}
                </div>

                {socialConnections.youtube?.is_connected ? (
                  <>
                    <div className="social-metrics">
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.youtube.follower_count)}</span>
                        <span className="metric-label">Subscribers</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.youtube.post_count)}</span>
                        <span className="metric-label">Videos</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.youtube.avg_likes_per_post)}</span>
                        <span className="metric-label">Avg Views</span>
                      </div>
                    </div>
                    <div className="social-card-actions">
                      <button
                        type="button"
                        className="sync-btn"
                        onClick={() => handleSyncPlatform('youtube')}
                        disabled={connectingPlatform === 'youtube'}
                      >
                        {connectingPlatform === 'youtube' ? 'Syncing...' : 'Refresh'}
                      </button>
                      <button
                        type="button"
                        className="disconnect-btn"
                        onClick={() => handleDisconnectPlatform('youtube')}
                      >
                        Disconnect
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="connect-btn youtube"
                    onClick={() => handleConnectPlatform('youtube')}
                    disabled={connectingPlatform === 'youtube'}
                  >
                    {connectingPlatform === 'youtube' ? 'Connecting...' : 'Connect YouTube'}
                  </button>
                )}
              </div>
              )}

              {/* Facebook */}
              {ENABLED_SOCIAL_PLATFORMS.includes('facebook') && (
              <div className={`social-connection-card ${socialConnections.facebook?.is_connected ? 'connected' : ''}`}>
                <div className="social-card-header">
                  <div className="social-icon facebook">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </div>
                  <div className="social-card-info">
                    <h4>Facebook</h4>
                    {socialConnections.facebook?.is_connected ? (
                      <span className="social-username">{socialConnections.facebook.display_name}</span>
                    ) : (
                      <span className="social-status">Not connected</span>
                    )}
                  </div>
                </div>

                {socialConnections.facebook?.is_connected ? (
                  <>
                    <div className="social-metrics">
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.facebook.follower_count)}</span>
                        <span className="metric-label">Followers</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.facebook.post_count)}</span>
                        <span className="metric-label">Recent posts</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{socialConnections.facebook.engagement_rate || 0}%</span>
                        <span className="metric-label">Engagement</span>
                      </div>
                    </div>
                    <RecentMediaGrid
                      items={socialConnections.facebook.recent_media}
                      label="Recent posts"
                      platformName="Facebook"
                    />
                    <div className="social-card-actions">
                      <button
                        type="button"
                        className="sync-btn"
                        onClick={() => handleSyncPlatform('facebook')}
                        disabled={connectingPlatform === 'facebook'}
                      >
                        {connectingPlatform === 'facebook' ? 'Syncing...' : 'Refresh'}
                      </button>
                      <button
                        type="button"
                        className="disconnect-btn"
                        onClick={() => handleDisconnectPlatform('facebook')}
                      >
                        Disconnect
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="connect-btn facebook"
                    onClick={() => handleConnectPlatform('facebook')}
                    disabled={connectingPlatform === 'facebook'}
                  >
                    {connectingPlatform === 'facebook' ? 'Connecting...' : 'Connect Facebook'}
                  </button>
                )}
                {!socialConnections.facebook?.is_connected && (
                  <p className="social-hint">
                    Connects the Facebook Page you post to as a creator.
                  </p>
                )}
              </div>
              )}

              {/* Snapchat */}
              {ENABLED_SOCIAL_PLATFORMS.includes('snapchat') && (
              <div className={`social-connection-card ${socialConnections.snapchat?.is_connected ? 'connected' : ''}`}>
                <div className="social-card-header">
                  <div className="social-icon snapchat">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.076-.375-.09-.84-.181-1.455-.181-.439 0-.9.061-1.365.181-.629.165-1.185.571-1.74.975-.811.585-1.65 1.191-3.024 1.191h-.06c-1.374 0-2.213-.606-3.024-1.191-.554-.404-1.11-.81-1.739-.975-.464-.119-.93-.181-1.364-.181-.66 0-1.11.104-1.456.181-.225.044-.405.076-.539.076-.24 0-.449-.12-.539-.39a2.7 2.7 0 0 1-.135-.554 8.82 8.82 0 0 0-.164-.569c-1.874-.284-2.906-.702-3.146-1.271a.506.506 0 0 1-.044-.225c-.016-.24.164-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809a3.72 3.72 0 0 1-.346-.12c-.823-.329-1.227-.719-1.212-1.168 0-.36.284-.689.734-.838a1.14 1.14 0 0 1 .51-.09c.119 0 .3.015.449.104.374.18.735.301 1.035.301.196 0 .314-.045.375-.075a8.52 8.52 0 0 1-.033-.57c-.104-1.628-.229-3.654.3-4.848C7.853 1.069 11.216.793 12.206.793z"/>
                    </svg>
                  </div>
                  <div className="social-card-info">
                    <h4>Snapchat</h4>
                    {socialConnections.snapchat?.is_connected ? (
                      <span className="social-username">@{socialConnections.snapchat.username}</span>
                    ) : (
                      <span className="social-status">Not connected</span>
                    )}
                  </div>
                  {socialConnections.snapchat?.is_verified && (
                    <span className="verified-badge" title="Verified Account">✓</span>
                  )}
                </div>

                {socialConnections.snapchat?.is_connected ? (
                  <>
                    <div className="social-metrics">
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.snapchat.follower_count)}</span>
                        <span className="metric-label">Subscribers</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.snapchat.post_count)}</span>
                        <span className="metric-label">Stories</span>
                      </div>
                    </div>
                    <div className="social-card-actions">
                      <button
                        type="button"
                        className="sync-btn"
                        onClick={() => handleSyncPlatform('snapchat')}
                        disabled={connectingPlatform === 'snapchat'}
                      >
                        {connectingPlatform === 'snapchat' ? 'Syncing...' : 'Refresh'}
                      </button>
                      <button
                        type="button"
                        className="disconnect-btn"
                        onClick={() => handleDisconnectPlatform('snapchat')}
                      >
                        Disconnect
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="connect-btn snapchat"
                    onClick={() => handleConnectPlatform('snapchat')}
                    disabled={connectingPlatform === 'snapchat'}
                  >
                    {connectingPlatform === 'snapchat' ? 'Connecting...' : 'Connect Snapchat'}
                  </button>
                )}
              </div>
              )}

              {/* X (Twitter) */}
              {ENABLED_SOCIAL_PLATFORMS.includes('twitter') && (
              <div className={`social-connection-card ${socialConnections.twitter?.is_connected ? 'connected' : ''}`}>
                <div className="social-card-header">
                  <div className="social-icon twitter">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </div>
                  <div className="social-card-info">
                    <h4>X (Twitter)</h4>
                    {socialConnections.twitter?.is_connected ? (
                      <span className="social-username">@{socialConnections.twitter.username}</span>
                    ) : (
                      <span className="social-status">Not connected</span>
                    )}
                  </div>
                  {socialConnections.twitter?.is_verified && (
                    <span className="verified-badge" title="Verified Account">✓</span>
                  )}
                </div>

                {socialConnections.twitter?.is_connected ? (
                  <>
                    <div className="social-metrics">
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.twitter.follower_count)}</span>
                        <span className="metric-label">Followers</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{formatNumber(socialConnections.twitter.post_count)}</span>
                        <span className="metric-label">Posts</span>
                      </div>
                      <div className="metric">
                        <span className="metric-value">{socialConnections.twitter.engagement_rate || 0}%</span>
                        <span className="metric-label">Engagement</span>
                      </div>
                    </div>
                    <div className="social-card-actions">
                      <button
                        type="button"
                        className="sync-btn"
                        onClick={() => handleSyncPlatform('twitter')}
                        disabled={connectingPlatform === 'twitter'}
                      >
                        {connectingPlatform === 'twitter' ? 'Syncing...' : 'Refresh'}
                      </button>
                      <button
                        type="button"
                        className="disconnect-btn"
                        onClick={() => handleDisconnectPlatform('twitter')}
                      >
                        Disconnect
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="connect-btn twitter"
                    onClick={() => handleConnectPlatform('twitter')}
                    disabled={connectingPlatform === 'twitter'}
                  >
                    {connectingPlatform === 'twitter' ? 'Connecting...' : 'Connect X (Twitter)'}
                  </button>
                )}
              </div>
              )}
            </div>
          </section>
          )}

          {/* Typical Rates */}
          <section className="form-section">
            <h2>Typical Rates</h2>
            <p className="section-subtitle">Set your rates for different content types (optional but recommended)</p>
            
            <div className="rates-list">
              {formData.rates.map((rate, index) => (
                <div key={index} className="rate-entry">
                  <div className="rate-fields">
                    <div className="form-group rate-type-group">
                      <label>Content Type</label>
                      <select
                        value={rate.type}
                        onChange={(e) => updateRate(index, 'type', e.target.value)}
                      >
                        {getAvailableRateTypes(rate.type).map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group rate-amount-group">
                      <label>Rate</label>
                      <div className="input-with-addon">
                        <span className="input-addon">$</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatMoney(rate.amount)}
                          onChange={(e) => updateRate(index, 'amount', e.target.value.replace(/[^\d]/g, ''))}
                          placeholder="500"
                        />
                      </div>
                    </div>
                  </div>
                  {formData.rates.length > 1 && (
                    <button
                      type="button"
                      className="remove-rate-btn"
                      onClick={() => removeRate(index)}
                      aria-label="Remove rate"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>

            {formData.rates.length < rateTypeOptions.length && (
              <button type="button" className="add-rate-btn" onClick={addRate}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add Another Rate
              </button>
            )}
          </section>

          {/* Past Partnerships */}
          <section className="form-section">
            <h2>Past Partnerships</h2>
            <p className="section-subtitle">Notable brands you've worked with (builds credibility)</p>
            
            <div className="partnerships-list">
              {formData.pastPartnerships.map((partnership, index) => (
                <div key={index} className="partnership-entry">
                  <div className="partnership-fields">
                    <div className="form-group partnership-brand-group">
                      <label>Brand Name</label>
                      <input
                        type="text"
                        value={partnership.brandName}
                        onChange={(e) => updatePartnership(index, 'brandName', e.target.value)}
                        placeholder="e.g., Nike, Pepsi, Samsung"
                      />
                    </div>
                    <div className="form-group partnership-type-group">
                      <label>Campaign Type</label>
                      <select
                        value={partnership.campaignType}
                        onChange={(e) => updatePartnership(index, 'campaignType', e.target.value)}
                      >
                        <option value="">Select type</option>
                        {campaignTypeOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group partnership-year-group">
                      <label>Year</label>
                      <select
                        value={partnership.year}
                        onChange={(e) => updatePartnership(index, 'year', e.target.value)}
                      >
                        <option value="">Year</option>
                        {[2026, 2025, 2024, 2023, 2022, 2021, 2020].map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {formData.pastPartnerships.length > 1 && (
                    <button
                      type="button"
                      className="remove-partnership-btn"
                      onClick={() => removePartnership(index)}
                      aria-label="Remove partnership"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button type="button" className="add-partnership-btn" onClick={addPartnership}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Partnership
            </button>
          </section>

          {/* Payment Details */}
          <section className="form-section">
            <h2>Payment Details</h2>
            <p className="section-subtitle">How would you like to receive payments? (kept private)</p>
            
            <div className="form-group">
              <label htmlFor="paymentMethod">Preferred Payment Method</label>
              <select
                id="paymentMethod"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleChange}
              >
                <option value="">Select payment method</option>
                <option value="paypal">PayPal</option>
                <option value="venmo">Venmo</option>
                <option value="zelle">Zelle</option>
                <option value="bank_transfer">Bank Transfer (ACH)</option>
                <option value="wire">Wire Transfer</option>
                <option value="check">Check</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Dynamic payment details based on selected method */}
            {formData.paymentMethod === 'paypal' && (
              <div className="form-group">
                <label htmlFor="paymentDetails">PayPal Email</label>
                <input
                  type="email"
                  id="paymentDetails"
                  name="paymentDetails"
                  value={formData.paymentDetails}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                />
              </div>
            )}

            {formData.paymentMethod === 'venmo' && (
              <div className="form-group">
                <label htmlFor="paymentDetails">Venmo Username</label>
                <input
                  type="text"
                  id="paymentDetails"
                  name="paymentDetails"
                  value={formData.paymentDetails}
                  onChange={handleChange}
                  placeholder="@YourVenmoHandle"
                />
              </div>
            )}

            {formData.paymentMethod === 'zelle' && (
              <div className="form-group">
                <label htmlFor="paymentDetails">Zelle Email or Phone</label>
                <input
                  type="text"
                  id="paymentDetails"
                  name="paymentDetails"
                  value={formData.paymentDetails}
                  onChange={handleChange}
                  placeholder="Email or phone number linked to Zelle"
                />
              </div>
            )}

            {formData.paymentMethod === 'bank_transfer' && (
              <div className="form-group">
                <label htmlFor="paymentDetails">Bank Account Details</label>
                <textarea
                  id="paymentDetails"
                  name="paymentDetails"
                  value={formData.paymentDetails}
                  onChange={handleChange}
                  placeholder="Bank Name&#10;Routing Number: XXXXXXXXX&#10;Account Number: XXXXXXXXX&#10;Account Type: Checking/Savings"
                  rows={4}
                />
              </div>
            )}

            {formData.paymentMethod === 'wire' && (
              <div className="form-group">
                <label htmlFor="paymentDetails">Wire Transfer Details</label>
                <textarea
                  id="paymentDetails"
                  name="paymentDetails"
                  value={formData.paymentDetails}
                  onChange={handleChange}
                  placeholder="Bank Name&#10;SWIFT/BIC Code: XXXXXXXX&#10;Account Number/IBAN: XXXXXXXXX&#10;Bank Address"
                  rows={4}
                />
              </div>
            )}

            {formData.paymentMethod === 'check' && (
              <div className="form-group">
                <label htmlFor="paymentDetails">Mailing Address for Checks</label>
                <textarea
                  id="paymentDetails"
                  name="paymentDetails"
                  value={formData.paymentDetails}
                  onChange={handleChange}
                  placeholder="Your Full Name&#10;Street Address&#10;City, State ZIP&#10;Country"
                  rows={4}
                />
              </div>
            )}

            {formData.paymentMethod === 'other' && (
              <div className="form-group">
                <label htmlFor="paymentDetails">Payment Details</label>
                <textarea
                  id="paymentDetails"
                  name="paymentDetails"
                  value={formData.paymentDetails}
                  onChange={handleChange}
                  placeholder="Please describe your preferred payment method and provide the necessary details..."
                  rows={3}
                />
              </div>
            )}

            {formData.paymentMethod && (
              <p className="input-hint">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight: '0.5rem', verticalAlign: 'middle'}}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                This information is encrypted and only shared with brands after you accept a deal
              </p>
            )}
          </section>

          {/* Save Button */}
          <div className="form-actions">
            <button type="submit" className="save-btn" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
        </div>
      </main>
    </div>
  );
}

export default InfluencerProfile;

